import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect, lazy, Suspense } from 'react'
import { Loader2 } from 'lucide-react'

const TenderQTOPage = lazy(() => import('@/pages/TenderQTOPage'))
// TAKEOFF-CONFIRM-PANEL-01: the estimator-in-the-loop take-off (tender QTO area)
const TenderTakeoffConfirmPage = lazy(() => import('@/pages/TenderTakeoffConfirmPage'))
const TenderEstimationPage = lazy(() => import('@/pages/TenderEstimationPage'))
const ItemCatalogPage = lazy(() => import('@/pages/ItemCatalogPage'))
// === Agent S4-AA (CRM-lite) BEGIN ===
const CRMLeadsPage = lazy(() => import('@/pages/CRM/Leads'))
const CRMLeadDetailPage = lazy(() => import('@/pages/CRM/LeadDetail'))
const CRMPipelinePage = lazy(() => import('@/pages/CRM/Pipeline'))
const CRMCampaignsPage = lazy(() => import('@/pages/CRM/Campaigns'))
const CRMCampaignBuilderPage = lazy(() => import('@/pages/CRM/CampaignBuilder'))
const CRMAnalyticsPage = lazy(() => import('@/pages/CRM/Analytics'))
// === Agent S4-AA (CRM-lite) END ===
// === Wave 11 Agent FIELD BEGIN ===
const FieldAppDashboard = lazy(() => import('@/pages/FieldApp/FieldAppDashboard'))
const FieldAppDailyReport = lazy(() => import('@/pages/FieldApp/DailyReportForm'))
const FieldAppPhotoCapture = lazy(() => import('@/pages/FieldApp/PhotoCapturePage'))
const FieldAppVoiceNote = lazy(() => import('@/pages/FieldApp/VoiceNotePage'))
const FieldAppPhotoGallery = lazy(() => import('@/pages/FieldApp/PhotoGalleryPage'))
// === Wave 11 Agent FIELD END ===

// Eager imports -- critical first-paint pages
import LoginPage from '@/pages/LoginPage'
import SignupPage from '@/pages/SignupPage'
import ForgotPasswordPage from '@/pages/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/ResetPasswordPage'
import DashboardPage from '@/pages/DashboardPage'
import BrandPage from '@/pages/BrandPage'
import { useAuthStore } from '@/stores/authStore'

// Lazy imports -- code-split per route
import { BreadcrumbProvider } from '@/components/BreadcrumbContext'
// === STAGE2-AGENT-B (Currency wiring) BEGIN ===
import { CurrencyProvider } from '@/contexts/CurrencyContext'
// === STAGE2-AGENT-B (Currency wiring) END ===
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage'))
const ProjectDetailsPage = lazy(() => import('@/pages/ProjectDetailsPage'))
const CompaniesPage = lazy(() => import('@/pages/CompaniesPage'))
const ClientsPage = lazy(() => import('@/pages/ClientsPage'))
const ClientDetailsPage = lazy(() => import('@/pages/ClientDetailsPage'))
const ConsultantsPage = lazy(() => import('@/pages/ConsultantsPage'))
const ConsultantDetailsPage = lazy(() => import('@/pages/ConsultantDetailsPage'))
const BOQPage = lazy(() => import('@/pages/BOQPage'))
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))
const TeamPage = lazy(() => import('@/pages/TeamPage'))
const ReportsPage = lazy(() => import('@/pages/ReportsPage'))
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'))
const SuppliersPage = lazy(() => import('@/pages/SuppliersPage'))
const SupplierDetailsPage = lazy(() => import('@/pages/SupplierDetailsPage'))
const ProjectPaymentsPage = lazy(() => import('@/pages/ProjectPaymentsPage'))
// TB-A: new IPC pages (singular /ipc/...) — canonical after C3 W2 route-dedup
// (legacy /ipcs + /ipcs/:ipcId now redirect here; IPCDetailsPage/IPCsListPage retired)
const IPCListPageV2 = lazy(() => import('@/pages/IPC/index'))
const IPCDetailPageV2 = lazy(() => import('@/pages/IPC/IPCDetailPage'))
const IPCCreatePageV2 = lazy(() => import('@/pages/IPC/IPCCreatePage'))
// === Wave 10 Agent F BEGIN ===
const IPCFidicListPage = lazy(
  () => import('@/pages/IPC/IPCFidicListPage'),
)
const IPCFidicDetailPage = lazy(
  () => import('@/pages/IPC/IPCFidicDetailPage'),
)
const IPCFidicNewWizardPage = lazy(
  () => import('@/pages/IPC/IPCFidicNewWizardPage'),
)
// === Wave 10 Agent F END ===
const PaymentSettingsPage = lazy(() => import('@/pages/PaymentSettingsPage'))
const SupplierInvoicesPage = lazy(() => import('@/pages/SupplierInvoicesPage'))
const InvoiceDetailsPage = lazy(() => import('@/pages/InvoiceDetailsPage'))
const CashFlowDashboardPage = lazy(() => import('@/pages/CashFlowDashboardPage'))
const ProjectCashFlowPage = lazy(() => import('@/pages/ProjectCashFlowPage'))
const PurchaseOrdersPage = lazy(() => import('@/pages/PurchaseOrdersPage'))
const PurchaseOrderDetailsPage = lazy(() => import('@/pages/PurchaseOrderDetailsPage'))
const AgingReportPage = lazy(() => import('@/pages/AgingReportPage'))
const SchedulePage = lazy(() => import('@/pages/SchedulePage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
// === Phase 2 Wave 5 Agent S1 BEGIN ===
const MFASettingsPage = lazy(() => import('@/pages/settings/MFASettingsPage'))
// === Phase 2 Wave 5 Agent S1 END ===
const PortfolioPage = lazy(() => import('@/pages/PortfolioPage'))
const OverheadPoolPage = lazy(() => import('@/pages/OverheadPoolPage'))
const ApprovalsInboxPage = lazy(() => import('@/pages/ApprovalsInboxPage'))
const BillingPricingPage = lazy(() => import('@/pages/BillingPricingPage'))
const BillingSuccessPage = lazy(() => import('@/pages/BillingSuccessPage'))
const BillingCancelPage = lazy(() => import('@/pages/BillingCancelPage'))
const BillingSubscriptionPage = lazy(() => import('@/pages/BillingSubscriptionPage'))
// === S4-P Email Signup + Billing (Sprint 4 Mega Wave 2) BEGIN ===
const SettingsBillingPage = lazy(() => import('@/pages/settings/Billing'))
const SettingsSubscriptionPage = lazy(() => import('@/pages/settings/Subscription'))
const SettingsUsagePage = lazy(() => import('@/pages/settings/Usage'))
// === Phase 2 Wave 6 Agent S3 BEGIN ===
const PasswordPolicyAdminPage = lazy(() => import('@/pages/settings/PasswordPolicyPage'))
// === Phase 2 Wave 6 Agent S3 END ===
// === Wave 11 Agent CUR BEGIN ===
const Wave11CurrencySettingsPage = lazy(() => import('@/pages/settings/CurrencySettingsPage'))
const Wave11HijriSettingsPage = lazy(() => import('@/pages/settings/HijriSettingsPage'))
// === Wave 11 Agent CUR END ===
// === S4-P Email Signup + Billing (Sprint 4 Mega Wave 2) END ===
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'))
const VerifyPendingPage = lazy(() => import('@/pages/VerifyPendingPage'))
const TendersPage = lazy(() => import('@/pages/TendersPage'))
const TenderDetailsPage = lazy(() => import('@/pages/TenderDetailsPage'))
const QualityInspectionsPage = lazy(() => import('@/pages/QualityInspectionsPage'))
const InspectionDetailsPage = lazy(() => import('@/pages/InspectionDetailsPage'))
const NCRsPage = lazy(() => import('@/pages/NCRsPage'))
const PunchListPage = lazy(() => import('@/pages/PunchListPage'))
const QualityDashboardPage = lazy(() => import('@/pages/QualityDashboardPage'))
const NCRDetailsPage = lazy(() => import('@/pages/NCRDetailsPage'))
// VariationOrdersPage + VODetailsPage retired in C3 W2 route-dedup (legacy
// /variation-orders/* now redirects to canonical /vo/*). VariationOrdersPage
// is still imported independently by projectRouteAliases for /changes/* views.
const BOQImporterPage = lazy(() => import('@/pages/BOQImporterPage'))
const BOQImportDetailsPage = lazy(() => import('@/pages/BOQImportDetailsPage'))
const BOQTemplateManagerPage = lazy(() => import('@/pages/BOQTemplateManagerPage'))
const BOQImporterPremium = lazy(() => import('@/pages/BOQImporter/BOQImporterPremium'))
// === Phase 2 Agent D1 BEGIN ===
// === Phase 2 Agent D1 END ===
const DMSDashboardPage = lazy(() => import('@/pages/DMSDashboardPage'))
const DrawingsRegisterPage = lazy(() => import('@/pages/DrawingsRegisterPage'))
const DrawingDetailPage = lazy(() => import('@/pages/DrawingDetailPage'))
// TB-B BIM activation
const BIMLibraryPage = lazy(() => import('@/pages/BIM/BIMLibrary'))
// === Wave 13 Agent BIM BEGIN ===
const Wave13BIMViewerPage = lazy(() => import('@/pages/qto/BIMViewerPage'))
// === Wave 13 Agent BIM END ===
const SubmittalsTrackerPage = lazy(() => import('@/pages/SubmittalsTrackerPage'))
const SubmittalDetailPage = lazy(() => import('@/pages/SubmittalDetailPage'))
const RFIInboxPage = lazy(() => import('@/pages/RFIInboxPage'))
const RFIDetailPage = lazy(() => import('@/pages/RFIDetailPage'))
const TransmittalsListPage = lazy(() => import('@/pages/TransmittalsListPage'))
const TransmittalCreatePage = lazy(() => import('@/pages/TransmittalCreatePage'))
const TransmittalDetailPage = lazy(() => import('@/pages/TransmittalDetailPage'))
const HSEDashboardPage = lazy(() => import('@/pages/HSEDashboardPage'))
const HSEPermitsPage = lazy(() => import('@/pages/HSEPermitsPage'))
const HSEPermitDetailsPage = lazy(() => import('@/pages/HSEPermitDetailsPage'))
const HSEToolboxTalksPage = lazy(() => import('@/pages/HSEToolboxTalksPage'))
const HSEToolboxTalkDetailsPage = lazy(() => import('@/pages/HSEToolboxTalkDetailsPage'))
const HSEIncidentsPage = lazy(() => import('@/pages/HSEIncidentsPage'))
const HSEIncidentDetailsPage = lazy(() => import('@/pages/HSEIncidentDetailsPage'))
const HSEInspectionsPage = lazy(() => import('@/pages/HSEInspectionsPage'))
const HSEInspectionDetailsPage = lazy(() => import('@/pages/HSEInspectionDetailsPage'))
const HSETrainingRecordsPage = lazy(() => import('@/pages/HSETrainingRecordsPage'))
// === Phase 2 Wave 5 Agent M1 BEGIN ===
const Wave5HSEDashboard = lazy(() => import('@/pages/HSE/Wave5Dashboard'))
const Wave5HSEPermitsList = lazy(() => import('@/pages/HSE/Wave5PermitsListPage'))
const Wave5HSEPermitDetail = lazy(() => import('@/pages/HSE/Wave5PermitDetailPage'))
const Wave5HSEIncidents = lazy(() => import('@/pages/HSE/Wave5IncidentsPage'))
const Wave5HSEToolboxTalks = lazy(() => import('@/pages/HSE/Wave5ToolboxTalksPage'))
// === Phase 2 Wave 5 Agent M1 END ===
// === AGENT_E (Tax Suite) BEGIN ===
const VATReturnsListPage = lazy(() => import('@/pages/VATReturnsListPage'))
const VATReturnDetailPage = lazy(() => import('@/pages/VATReturnDetailPage'))
const VATConfigPage = lazy(() => import('@/pages/VATConfigPage'))
const CTReturnsListPage = lazy(() => import('@/pages/CTReturnsListPage'))
const CTReturnDetailPage = lazy(() => import('@/pages/CTReturnDetailPage'))
// === AGENT_E (Tax Suite) END ===

// === AGENT_A (Field App) BEGIN ===
// FieldReportsListPage retired in C3 W2 route-dedup (/field/reports -> /field-app);
// the per-report detail page is kept (no field-app equivalent).
const FieldReportDetailPage = lazy(() => import('@/pages/FieldReportDetailPage'))
const PendingSyncPage = lazy(() => import('@/pages/PendingSyncPage'))
// === AGENT_A (Field App) END ===

// === AGENT_D (Invoice Scanning) BEGIN ===
const InvoiceScansListPage = lazy(() => import('@/pages/InvoiceScansListPage'))
const InvoiceScanReviewPage = lazy(() => import('@/pages/InvoiceScanReviewPage'))
const InvoiceScanStatsPage = lazy(() => import('@/pages/InvoiceScanStatsPage'))
// === AGENT_D (Invoice Scanning) END ===

// === AGENT_A (M2.8 Workforce + Payroll) BEGIN ===
const EmployeesListPage = lazy(() => import('@/pages/EmployeesListPage'))
const EmployeeDetailPage = lazy(() => import('@/pages/EmployeeDetailPage'))
const DepartmentsTreePage = lazy(() => import('@/pages/DepartmentsTreePage'))
const LeaveRequestsPage = lazy(() => import('@/pages/LeaveRequestsPage'))
const AttendanceDashboardPage = lazy(() => import('@/pages/AttendanceDashboardPage'))
const PayrollPeriodsPage = lazy(() => import('@/pages/PayrollPeriodsPage'))
const PayrollRunWizardPage = lazy(() => import('@/pages/PayrollRunWizardPage'))
const PayslipDetailPage = lazy(() => import('@/pages/PayslipDetailPage'))
const WPSExportPage = lazy(() => import('@/pages/WPSExportPage'))
const GratuityCalculatorPage = lazy(() => import('@/pages/GratuityCalculatorPage'))
// === AGENT_A (M2.8 Workforce + Payroll) END ===

// === Wave 11 Agent HR BEGIN ===
const WorkforceDashboard = lazy(() => import('@/pages/Workforce/WorkforceDashboard'))
const AntiGhostWorkerPage = lazy(() => import('@/pages/Workforce/AntiGhostWorkerPage'))
const ProjectLaborCostPage = lazy(() => import('@/pages/Workforce/ProjectLaborCostPage'))
const WorkforceProductivityPage = lazy(() => import('@/pages/Workforce/ProductivityPage'))
const WorkforcePayrollPage = lazy(() => import('@/pages/Workforce/WorkforcePayrollPage'))
const WPSEnterprisePage = lazy(() => import('@/pages/Workforce/WPSEnterprisePage'))
// === Wave 11 Agent HR END ===

// === AGENT_B (Subcontractor) BEGIN ===
const SubcontractorsListPage = lazy(() => import('@/pages/SubcontractorsListPage'))
const SubcontractorDetailPage = lazy(() => import('@/pages/SubcontractorDetailPage'))
const SubcontractsListPage = lazy(() => import('@/pages/SubcontractsListPage'))
const SubcontractCreatePage = lazy(() => import('@/pages/SubcontractCreatePage'))
const SubcontractDetailPage = lazy(() => import('@/pages/SubcontractDetailPage'))
const SubcontractorPaymentsListPage = lazy(() => import('@/pages/SubcontractorPaymentsListPage'))
const SubcontractorPaymentCreatePage = lazy(() => import('@/pages/SubcontractorPaymentCreatePage'))
const SubcontractorPaymentDetailPage = lazy(() => import('@/pages/SubcontractorPaymentDetailPage'))
// === AGENT_B (Subcontractor) END ===

// === Phase 2 Wave 6 Agent M2 (Subcontractor Management) BEGIN ===
const SubcontractorsListPageV2 = lazy(
  () => import('@/pages/Subcontractors/SubcontractorsListPage'),
)
const SubcontractorDetailPageV2 = lazy(
  () => import('@/pages/Subcontractors/SubcontractorDetailPage'),
)
const SubcontractorsContractsPageV2 = lazy(
  () => import('@/pages/Subcontractors/ContractsPage'),
)
const SubcontractorsPaymentsPageV2 = lazy(
  () => import('@/pages/Subcontractors/PaymentCertificatesPage'),
)
// === Phase 2 Wave 6 Agent M2 (Subcontractor Management) END ===

// === AGENT_G (Sentry frontend + PostHog) BEGIN ===
import { RouterPageviewTracker } from '@/components/RouterPageviewTracker'
// === AGENT_G (Sentry frontend + PostHog) END ===

// === AGENT_H (Frontend Set 1 — M3.5 Predictive / M1.7 Locale / M3.7 Chatbot) BEGIN ===
const PredictiveDashboardPage = lazy(() => import('@/pages/PredictiveDashboardPage'))
const PredictiveAlertsPage = lazy(() => import('@/pages/PredictiveAlertsPage'))
const PredictiveProjectPage = lazy(() => import('@/pages/PredictiveProjectPage'))
const PredictiveHistoryPage = lazy(() => import('@/pages/PredictiveHistoryPage'))
// === AGENT_S4D (S4-D AI Predictive Project Analytics) BEGIN ===
const PredictiveV2DashboardPage = lazy(() => import('@/pages/Predictive/index'))
const PredictiveV2ProjectPage = lazy(() => import('@/pages/Predictive/ProjectPredictions'))
const PredictiveV2ScenarioPage = lazy(() => import('@/pages/Predictive/ScenarioModeler'))
// === AGENT_S4D (S4-D AI Predictive Project Analytics) END ===
// === Wave 10 Agent G (Project Health Dashboard) BEGIN ===
const HealthDashboardPage = lazy(() => import('@/pages/Health/HealthDashboardPage'))
const PortfolioOverviewPage = lazy(() => import('@/pages/Health/PortfolioOverviewPage'))
// === Wave 10 Agent G (Project Health Dashboard) END ===
const LocaleSettingsPage = lazy(() => import('@/pages/LocaleSettingsPage'))
const HijriCalendarPage = lazy(() => import('@/pages/HijriCalendarPage'))
const CurrencyConversionsPage = lazy(() => import('@/pages/CurrencyConversionsPage'))
const ChatSessionsPage = lazy(() => import('@/pages/ChatSessionsPage'))
const ChatSessionPage = lazy(() => import('@/pages/ChatSessionPage'))
// === AGENT_H END ===

// === Wave 10 Agent A BEGIN ===
const Wave10AChatbotPage = lazy(() => import('@/pages/AIChatbot/ChatbotPage'))
// === Wave 10 Agent A END ===

// === AGENT_I (Frontend Set 2) BEGIN ===
const BOQWizardStartPage = lazy(() => import('@/pages/BOQWizardStartPage'))
const BOQWizardUploadPage = lazy(() => import('@/pages/BOQWizardUploadPage'))
const BOQWizardTemplatePage = lazy(() => import('@/pages/BOQWizardTemplatePage'))
const BOQWizardMappingPage = lazy(() => import('@/pages/BOQWizardMappingPage'))
const BOQWizardPreviewPage = lazy(() => import('@/pages/BOQWizardPreviewPage'))
const BOQWizardApplyPage = lazy(() => import('@/pages/BOQWizardApplyPage'))
const DMSLettersInboxPage = lazy(() => import('@/pages/DMSLettersInboxPage'))
const DMSLetterComposePage = lazy(() => import('@/pages/DMSLetterComposePage'))
const DMSLetterDetailPage = lazy(() => import('@/pages/DMSLetterDetailPage'))
const RBACSecurityDashboardPage = lazy(() => import('@/pages/RBACSecurityDashboardPage'))
const RBAC2FASetupPage = lazy(() => import('@/pages/RBAC2FASetupPage'))
const RBACAuditLogPage = lazy(() => import('@/pages/RBACAuditLogPage'))
const RBACSessionsPage = lazy(() => import('@/pages/RBACSessionsPage'))
const RBACPoliciesPage = lazy(() => import('@/pages/RBACPoliciesPage'))
const RBACPolicyDetailPage = lazy(() => import('@/pages/RBACPolicyDetailPage'))
const RBACAISuggestionsPage = lazy(() => import('@/pages/RBACAISuggestionsPage'))
const RBACAnomaliesPage = lazy(() => import('@/pages/RBACAnomaliesPage'))
const MaterialCatalogPage = lazy(() => import('@/pages/MaterialCatalogPage'))
const MaterialRequestsPage = lazy(() => import('@/pages/MaterialRequestsPage'))
const MaterialConsumptionPage = lazy(() => import('@/pages/MaterialConsumptionPage'))
const MaterialTheftAlertsPage = lazy(() => import('@/pages/MaterialTheftAlertsPage'))
// === Wave 11 Agent MAT BEGIN ===
const Wave11MatRequestsPage = lazy(() => import('@/pages/Materials/MaterialRequestsPage'))
const Wave11MatRequestDetailPage = lazy(() => import('@/pages/Materials/RequestDetailPage'))
const Wave11MatRequestFormPage = lazy(() => import('@/pages/Materials/RequestFormPage'))
const Wave11MatConsumptionPage = lazy(() => import('@/pages/Materials/ConsumptionLogPage'))
const Wave11MatTheftAlertsPage = lazy(() => import('@/pages/Materials/TheftAlertsPage'))
const Wave11MatReconciliationPage = lazy(() => import('@/pages/Materials/ReconciliationPage'))
// === Wave 11 Agent MAT END ===
const SustainabilityDashboardPage = lazy(() => import('@/pages/SustainabilityDashboardPage'))
const SustainabilityCertificationsPage = lazy(() => import('@/pages/SustainabilityCertificationsPage'))
const SustainabilityCarbonPage = lazy(() => import('@/pages/SustainabilityCarbonPage'))
const SustainabilityResourcesPage = lazy(() => import('@/pages/SustainabilityResourcesPage'))
// === AGENT_S4_M (Sustainability v2) BEGIN ===
const SustainabilityV2DashboardPage = lazy(() => import('@/pages/Sustainability/index'))
const SustainabilityV2EstidamaPage = lazy(() => import('@/pages/Sustainability/EstidamaTracker'))
const SustainabilityV2LEEDPage = lazy(() => import('@/pages/Sustainability/LEEDTracker'))
const SustainabilityV2CarbonPage = lazy(() => import('@/pages/Sustainability/CarbonFootprint'))
// === AGENT_S4_M (Sustainability v2) END ===
// === Wave 8/9 Agent M11 BEGIN ===
const SustainabilityM11Dashboard = lazy(() => import('@/pages/SustainabilityM11/SustainabilityDashboard'))
const SustainabilityM11Estidama = lazy(() => import('@/pages/SustainabilityM11/EstidamaChecklistPage'))
const SustainabilityM11LEED = lazy(() => import('@/pages/SustainabilityM11/LEEDChecklistPage'))
const SustainabilityM11Carbon = lazy(() => import('@/pages/SustainabilityM11/CarbonReportPage'))
// === Wave 8/9 Agent M11 END ===
// === AGENT_I (Frontend Set 2) END ===

// === AGENT_J (Frontend Set 3 — Cost Estimation / QTO / Govt) BEGIN ===
const CostEstimationListPage = lazy(() => import('@/pages/CostEstimationListPage'))
const CostEstimationDetailsPage = lazy(() => import('@/pages/CostEstimationDetailsPage'))
const CostEstimationNewPage = lazy(() => import('@/pages/CostEstimationNewPage'))
const CostHistoricalRatesPage = lazy(() => import('@/pages/CostHistoricalRatesPage'))
const CostBenchmarksPage = lazy(() => import('@/pages/CostBenchmarksPage'))
const QTODrawingsPage = lazy(() => import('@/pages/QTODrawingsPage'))
const QTODrawingViewerPage = lazy(() => import('@/pages/QTODrawingViewerPage'))
// Wave 15-2: canvas-based measurement tools (distance / polyline / polygon / count)
const QTOCanvasMeasurePage = lazy(() => import('@/pages/DrawingsPage'))
const QTOMeasurementsPage = lazy(() => import('@/pages/QTOMeasurementsPage'))
const QTOReportsPage = lazy(() => import('@/pages/QTOReportsPage'))
// === STAGE3-S6-BOQ-MAPPING BEGIN ===
const BoqMappingPage = lazy(() => import('@/pages/BoqMappingPage'))
// === STAGE3-S6-BOQ-MAPPING END ===
// === STAGE4-P2E (Pricing UI) BEGIN ===
// === STAGE4-P2E END ===
// === STAGE4-P5B (One-Click Generate UI) BEGIN ===
const QTOGenerateWizardPage = lazy(() => import('@/pages/QTOGenerateWizardPage'))
// === STAGE4-P5B END ===
// === AGENT_S4H (Asset / Equipment frontend) BEGIN ===
const EquipmentListPage = lazy(() => import('@/pages/Equipment'))
const EquipmentDetailPage = lazy(() => import('@/pages/Equipment/EquipmentDetail'))
const EquipmentGPSMapPage = lazy(() => import('@/pages/Equipment/GPSMap'))
const EquipmentMaintenanceSchedulePage = lazy(
  () => import('@/pages/Equipment/MaintenanceSchedule'),
)
const EquipmentFuelTrackingPage = lazy(
  () => import('@/pages/Equipment/FuelTracking'),
)
const EquipmentCostAllocationPage = lazy(
  () => import('@/pages/Equipment/CostAllocation'),
)
// === AGENT_S4H (Asset / Equipment frontend) END ===
const GovtIntegrationsDashboard = lazy(() => import('@/pages/GovtIntegrationsDashboard'))
const MOLLaborCompliancePage = lazy(() => import('@/pages/MOLLaborCompliancePage'))
const WPSEnhancedPage = lazy(() => import('@/pages/WPSEnhancedPage'))
const MunicipalityPermitsPage = lazy(() => import('@/pages/MunicipalityPermitsPage'))
const EmiratesIDVerificationPage = lazy(() => import('@/pages/EmiratesIDVerificationPage'))
const TaxAuthorityFilingPage = lazy(() => import('@/pages/TaxAuthorityFilingPage'))
// === AGENT_J (Frontend Set 3) END ===

// === Phase 2 Agent F4 ===
// F4 /variations/* pages retired in C3 W2 route-dedup (redirected to canonical /vo/*).
// === Phase 2 Agent F4 END ===
// === RUN10-FE-2 (Tender v2 + VO v2) BEGIN ===
const Run10TenderListPage = lazy(() => import('@/pages/tender/TenderListPage'))
const Run10TenderCreatePage = lazy(() => import('@/pages/tender/TenderCreatePage'))
const Run10TenderEvaluatePage = lazy(() => import('@/pages/tender/TenderEvaluatePage'))
const Run10TenderAwardPage = lazy(() => import('@/pages/tender/TenderAwardPage'))
const Run10VOListPage = lazy(() => import('@/pages/variation-orders/VOListPage'))
const Run10VOCreatePage = lazy(() => import('@/pages/variation-orders/VOCreatePage'))
const Run10VOApprovePage = lazy(() => import('@/pages/variation-orders/VOApprovePage'))
const Run10VOHistoryPage = lazy(() => import('@/pages/variation-orders/VOHistoryPage'))
// === RUN10-FE-2 END ===
// === Agent W2-K start ===
const W2KVODetailPage = lazy(() => import('@/pages/variation-orders/VODetailPage'))
// === Agent W2-K end ===
// === AGENT_W3_L (Tender Bidding) BEGIN ===
const TenderBiddingDashboardPage = lazy(() => import('@/pages/TenderBidding'))
const TenderBiddingStrategyPage = lazy(() => import('@/pages/TenderBidding/strategy'))
const TenderBiddingBondsPage = lazy(() => import('@/pages/TenderBidding/bonds'))
const TenderBiddingPQPage = lazy(() => import('@/pages/TenderBidding/prequalifications'))
// === AGENT_W3_L (Tender Bidding) END ===
// === AGENT_FE-3 (Run #10 — QA/QC + Set 3 polish) BEGIN ===
const QAQCInspectionsPage = lazy(() => import('@/pages/qa-qc/InspectionsPage'))
const QAQCChecklistsPage = lazy(() => import('@/pages/qa-qc/ChecklistsPage'))
const QAQCNCRsPage = lazy(() => import('@/pages/qa-qc/NCRsPage'))
const QAQCAuditsPage = lazy(() => import('@/pages/qa-qc/AuditsPage'))
const QAQCReportsPage = lazy(() => import('@/pages/qa-qc/ReportsPage'))
const QAQCSettingsPage = lazy(() => import('@/pages/qa-qc/SettingsPage'))
// === AGENT_S4_G (QA/QC Phase 2 FE) BEGIN ===
const QAQCDashboardPageNew = lazy(() => import('@/pages/QAQC'))
const QAQCITPsPage = lazy(() => import('@/pages/QAQC/ITPs'))
const QAQCNCRsPageNew = lazy(() => import('@/pages/QAQC/NCRs'))
const QAQCPunchListsPage = lazy(() => import('@/pages/QAQC/PunchLists'))
const QAQCMaterialTestingPageNew = lazy(() => import('@/pages/QAQC/MaterialTesting'))
const QAQCConcretePourLogPage = lazy(() => import('@/pages/QAQC/ConcretePourLog'))
const QAQCComplianceReportsPage = lazy(() => import('@/pages/QAQC/ComplianceReports'))
// === AGENT_S4_G (QA/QC Phase 2 FE) END ===
// === Wave 11 Agent QC2 BEGIN ===
const Wave11MaterialTestsPage = lazy(() => import('@/pages/QAQC/MaterialTestsPage'))
const Wave11CubeTestsPage = lazy(() => import('@/pages/QAQC/CubeTestsPage'))
const Wave11SlumpTestsPage = lazy(() => import('@/pages/QAQC/SlumpTestsPage'))
const Wave11ConcretePourLogPage = lazy(() => import('@/pages/QAQC/ConcretePourLogPage'))
const Wave11MixDesignLibraryPage = lazy(() => import('@/pages/QAQC/MixDesignLibraryPage'))
// === Wave 11 Agent QC2 END ===
const QTODrawingUploadPage = lazy(() => import('@/pages/qto/DrawingUploadPage'))
// === Phase 2 Agent F1 BEGIN ===
const QTOAutoMappingPage = lazy(() => import('@/pages/qto/AutoMappingPage'))
// === Phase 2 Agent F1 END ===
// === QTO Package Intake Preview BEGIN ===
const QTOPackageIntakePreviewPage = lazy(
  () => import('@/pages/qto/PackageIntakePreviewPage'),
)
// === QTO Package Intake Preview END ===
const CostEstimationComparisonPage = lazy(() => import('@/pages/cost-estimation/ComparisonPage'))
const GovtComplianceDashboardPage = lazy(() => import('@/pages/govt/ComplianceDashboardPage'))
// === AGENT_FE-3 (Run #10) END ===
// === AGENT_S4_C (Sprint 4 -- UAE Government Integration MVP) BEGIN ===
const GovernmentOverviewPage = lazy(() => import('@/pages/Government'))
const GovernmentMoHREPage = lazy(() => import('@/pages/Government/MoHRE'))
const GovernmentWPSPage = lazy(() => import('@/pages/Government/WPS'))
const GovernmentEmaraTaxPage = lazy(() => import('@/pages/Government/EmaraTax'))
const GovernmentMunicipalityPage = lazy(
  () => import('@/pages/Government/Municipality'),
)
// === AGENT_S4_C (Sprint 4 -- UAE Government Integration MVP) END ===
// === AGENT_FE-1 (Run #10 — Client Portal) BEGIN ===
const PortalLoginPage = lazy(() => import('@/pages/client-portal/PortalLoginPage'))
const PortalSettingsPage = lazy(() => import('@/pages/client-portal/PortalSettingsPage'))
// === S4-N (Sprint 4 -- polished portal pages live in src/portal/) ===
const PortalDashboardPage = lazy(() => import('@/portal/pages/Dashboard'))
const PortalApprovalsPage = lazy(() => import('@/portal/pages/Approvals'))
const PortalRFIsPage = lazy(() => import('@/portal/pages/RFIs'))
const PortalProgressPage = lazy(() => import('@/portal/pages/Progress'))
const PortalDocumentsPage = lazy(() => import('@/portal/pages/DocumentLibrary'))
const PortalReportsPage = lazy(() => import('@/portal/pages/Reports'))
import PortalProtectedRoute from '@/components/portal/PortalProtectedRoute'
import { BrandingProvider } from '@/portal/branding/BrandingProvider'
// === AGENT_FE-1 (Run #10 — Client Portal) END ===
// === Wave 10 Agent D BEGIN ===
const Wave10DMobileDashboard = lazy(() => import('@/pages/ClientPortal/MobileDashboard'))
const Wave10DApprovalsPage = lazy(() => import('@/pages/ClientPortal/ApprovalsPage'))
const Wave10DNotificationsPage = lazy(() => import('@/pages/ClientPortal/NotificationsPage'))
// === Wave 10 Agent D END ===

import { buildProcurementRoutes } from '@/routes/procurementRoutes'
import { renderGroupRouteAliases } from '@/routes/groupRouteAliases'
import { renderProjectRouteAliases } from '@/routes/projectRouteAliases'
// === Wave C / C3 (W2 route-dedup) ===
// Param-preserving redirect for retired duplicate route clusters so old
// bookmarks keep resolving to the canonical path (no dead links).
import RedirectWithParams from '@/routes/RedirectWithParams'

// === STAGE4-P5E (Demo Mode) BEGIN ===
const DemoExperiencePage = lazy(() => import('@/pages/DemoExperiencePage'))
// === STAGE4-P5E (Demo Mode) END ===

// === Phase 2 Agent F2 BEGIN ===
const AICostEstimationPage = lazy(
  () => import('@/pages/AICostEstimation/EstimationPage'),
)
// === Phase 2 Agent F2 END ===

// === Phase 2 Wave 5 Agent E3 BEGIN ===
const EstimationAnalyticsPage = lazy(
  () => import('@/pages/Analytics/EstimationAnalyticsPage'),
)
// === Phase 2 Wave 5 Agent E3 END ===

// === Phase 2 Wave 7 Agent M7 BEGIN ===
const DMSPremiumDashboard = lazy(
  () => import('@/pages/DMSPremium/DMSDashboard'),
)
const DMSPremiumDrawings = lazy(
  () => import('@/pages/DMSPremium/DrawingsPremiumPage'),
)
const DMSPremiumSubmittals = lazy(
  () => import('@/pages/DMSPremium/SubmittalsPage'),
)
const DMSPremiumSubmittalDetail = lazy(
  () => import('@/pages/DMSPremium/SubmittalDetailPage'),
)
const DMSPremiumRFIs = lazy(
  () => import('@/pages/DMSPremium/RFIsPage'),
)
const DMSPremiumRFIDetail = lazy(
  () => import('@/pages/DMSPremium/RFIDetailPage'),
)
const DMSPremiumTransmittals = lazy(
  () => import('@/pages/DMSPremium/TransmittalsPage'),
)
const DMSPremiumTransmittalNew = lazy(
  () => import('@/pages/DMSPremium/TransmittalNewPage'),
)
// === Phase 2 Wave 7 Agent M7 END ===

// === Wave 14 Agent POLISH BEGIN ===
// Wave 14: route wiring for pages that exist on disk but were never wired
// in App.tsx (lost during prior wave conflict resolutions). All entries are
// additive; no existing routes are touched.
const W14ProcurementDashboard = lazy(() => import('@/pages/Procurement/ProcurementDashboard'))
const W14ProcurementSuppliers = lazy(() => import('@/pages/Procurement/SuppliersPage'))
const W14ProcurementPOs = lazy(() => import('@/pages/Procurement/POsPage'))
const W14ProcurementGRNs = lazy(() => import('@/pages/Procurement/GRNsPage'))
const W14ProcurementAnalytics = lazy(() => import('@/pages/ProcurementAnalyticsPage'))
const W14RBACPremium = lazy(() => import('@/pages/Admin/RBACPremiumPage'))
const W14RBACRoleBuilder = lazy(() => import('@/pages/Admin/RoleBuilderPage'))
const W14RBACAccessAnomalies = lazy(() => import('@/pages/Admin/AccessAnomaliesPage'))
const W14RBACApprovalWorkflows = lazy(() => import('@/pages/Admin/ApprovalWorkflowsPage'))
const W14IndustryBenchmarks = lazy(() => import('@/pages/Analytics/IndustryBenchmarksPage'))
const W14BOQRevisions = lazy(() => import('@/pages/BOQ/BOQRevisionsPage'))
const W14CostComparison = lazy(() => import('@/pages/CostComparison/CostComparisonPage'))
const W14CostAlerts = lazy(() => import('@/pages/CostAlerts/index'))
const W14EstRiskAssessment = lazy(() => import('@/pages/Estimation/RiskAssessmentPage'))
const W14EstScenarios = lazy(() => import('@/pages/Estimation/ScenariosPage'))
const W14EstTemplates = lazy(() => import('@/pages/Estimation/TemplatesLibraryPage'))
const W14TaxCT = lazy(() => import('@/pages/Tax/CTReturnPage'))
const W14TaxVAT = lazy(() => import('@/pages/Tax/VATReturnPage'))
const W14TaxOwnerVATRefund = lazy(() => import('@/pages/Tax/OwnerVATRefundPage'))
const W14TaxComplianceDashboard = lazy(() => import('@/pages/Tax/TaxComplianceDashboard'))
const W14TendersBiddingPipeline = lazy(() => import('@/pages/Tenders/BiddingPipelinePage'))
const W14TendersBidPreparation = lazy(() => import('@/pages/Tenders/BidPreparationPage'))
const W14TendersBidOutcomes = lazy(() => import('@/pages/Tenders/BidOutcomesPage'))
const W14TendersCompetitorIntel = lazy(() => import('@/pages/Tenders/CompetitorIntelPage'))
const W14TendersDocLibrary = lazy(() => import('@/pages/Tenders/DocLibraryPage'))
const W14QTO4D5D = lazy(() => import('@/pages/qto/QTO4D5DPage'))
const W14QTOReview = lazy(() => import('@/pages/QTOReviewPage'))
const W14QTOMappingSession = lazy(() => import('@/pages/QtoMappingSessionPage'))
const W14SettingsCrowdPricing = lazy(() => import('@/pages/settings/CrowdPricingSettingsPage'))
const W14SettingsDMSBridge = lazy(() => import('@/pages/settings/DMSBridgePage'))
const W14SettingsRegional = lazy(() => import('@/pages/settings/RegionalSettings'))
const W14SettingsSecurity = lazy(() => import('@/pages/settings/SecurityPage'))
const W14GovDashboard = lazy(() => import('@/pages/Government/GovDashboard'))
const W14GovVisas = lazy(() => import('@/pages/Government/VisasPage'))
const W14MaterialsWarehouses = lazy(() => import('@/pages/Materials/Warehouses'))
const W14MaterialsTakeoff = lazy(() => import('@/pages/Materials/MaterialTakeoffPage'))
const W14MaterialsSiteConsumption = lazy(() => import('@/pages/Materials/SiteConsumption'))
const W14MaterialsTheftDetection = lazy(() => import('@/pages/Materials/TheftDetection'))
const W14BillingDashboard = lazy(() => import('@/pages/Billing/BillingDashboard'))
const W14BillingInvoices = lazy(() => import('@/pages/Billing/InvoicesPage'))
const W14BillingUpgrade = lazy(() => import('@/pages/Billing/UpgradePage'))
// === Wave 14 Agent POLISH END ===


function PageLoader() {
  return (
    <div className="flex items-center justify-center h-screen bg-gray-50">
      <Loader2 className="w-10 h-10 animate-spin text-primary-600" />
    </div>
  )
}

function App() {
  const { i18n } = useTranslation()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  useEffect(() => {
    document.documentElement.lang = i18n.language
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr'
  }, [i18n.language])

  return (
    <BrowserRouter>
      {/* === STAGE2-AGENT-B (Currency wiring) BEGIN === */}
      <CurrencyProvider>
      {/* === STAGE2-AGENT-B (Currency wiring) END === */}
      <BreadcrumbProvider>
        <RouterPageviewTracker />
        <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
          <Route path="/signup" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/brand" element={<BrandPage />} />
          <Route path="/dashboard" element={isAuthenticated ? <DashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/demo" element={isAuthenticated ? <DemoExperiencePage /> : <Navigate to="/login" replace />} />
          <Route path="/projects" element={isAuthenticated ? <ProjectsPage /> : <Navigate to="/login" replace />} />
          <Route path="/projects/:projectId" element={isAuthenticated ? <ProjectDetailsPage /> : <Navigate to="/login" replace />} />
          <Route path="/projects/:projectId/boq" element={isAuthenticated ? <BOQPage /> : <Navigate to="/login" replace />} />
          <Route path="/companies" element={isAuthenticated ? <CompaniesPage /> : <Navigate to="/login" replace />} />
          <Route path="/clients" element={isAuthenticated ? <ClientsPage /> : <Navigate to="/login" replace />} />
          <Route path="/clients/:clientName" element={isAuthenticated ? <ClientDetailsPage /> : <Navigate to="/login" replace />} />
          <Route path="/consultants" element={isAuthenticated ? <ConsultantsPage /> : <Navigate to="/login" replace />} />
          <Route path="/consultants/:consultantName" element={isAuthenticated ? <ConsultantDetailsPage /> : <Navigate to="/login" replace />} />
          <Route path="/suppliers" element={isAuthenticated ? <SuppliersPage /> : <Navigate to="/login" replace />} />
          <Route path="/suppliers/:supplierName" element={isAuthenticated ? <SupplierDetailsPage /> : <Navigate to="/login" replace />} />
          <Route path="/profile" element={isAuthenticated ? <ProfilePage /> : <Navigate to="/login" replace />} />
          <Route path="/team" element={isAuthenticated ? <TeamPage /> : <Navigate to="/login" replace />} />
          <Route path="/reports" element={isAuthenticated ? <ReportsPage /> : <Navigate to="/login" replace />} />
          <Route path="/notifications" element={isAuthenticated ? <NotificationsPage /> : <Navigate to="/login" replace />} />
          <Route path="/projects/:projectId/payments" element={isAuthenticated ? <ProjectPaymentsPage /> : <Navigate to="/login" replace />} />
          {/* === Wave C / C3 (W2 route-dedup) — IPC: canonical = V2 /ipc; legacy /ipcs redirected === */}
          {/* (/ipcs/new historically matched :ipcId; the param redirect lands it on /ipc/new which is the real V2 create page) */}
          <Route path="/ipcs/:ipcId" element={<RedirectWithParams to="/ipc/:ipcId" />} />
          <Route path="/projects/:projectId/payment-settings" element={isAuthenticated ? <PaymentSettingsPage /> : <Navigate to="/login" replace />} />
          <Route path="/supplier-invoices" element={isAuthenticated ? <SupplierInvoicesPage /> : <Navigate to="/login" replace />} />
          {/* === DEAD-CREATE-ROUTES-01 BEGIN === */}
          {/* `new` BEFORE the dynamic `:invoiceId` (the /dms/transmittals and
              /procurement/rfqs precedent). Router v7 ranks static above dynamic
              regardless of order, so this is documentation — but without the
              route at all, `/supplier-invoices/new` fell through to
              :invoiceId, Number('new') is NaN, the query is disabled and the
              page rendered a not-found card. That is the dead target of the
              mobile bottom-bar entry 'فاتورة جديدة' (nav/BottomNav/data.ts).
              The list page reads the path and opens its create modal. */}
          <Route path="/supplier-invoices/new" element={isAuthenticated ? <SupplierInvoicesPage /> : <Navigate to="/login" replace />} />
          {/* === DEAD-CREATE-ROUTES-01 END === */}
          <Route path="/supplier-invoices/:invoiceId" element={isAuthenticated ? <InvoiceDetailsPage /> : <Navigate to="/login" replace />} />
          <Route path="/cashflow" element={isAuthenticated ? <CashFlowDashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/projects/:projectId/cashflow" element={isAuthenticated ? <ProjectCashFlowPage /> : <Navigate to="/login" replace />} />
          <Route path="/purchase-orders" element={isAuthenticated ? <PurchaseOrdersPage /> : <Navigate to="/login" replace />} />
          {/* === DEAD-CREATE-ROUTES-01 BEGIN === */}
          {/* Dead target of the command-palette entry 'أمر شراء جديد'
              (components/CommandPalette/commandRegistry.ts). Same fall-through
              as /supplier-invoices/new above. */}
          <Route path="/purchase-orders/new" element={isAuthenticated ? <PurchaseOrdersPage /> : <Navigate to="/login" replace />} />
          {/* === DEAD-CREATE-ROUTES-01 END === */}
          <Route path="/purchase-orders/:poId" element={isAuthenticated ? <PurchaseOrderDetailsPage /> : <Navigate to="/login" replace />} />
          <Route path="/aging" element={isAuthenticated ? <AgingReportPage /> : <Navigate to="/login" replace />} />
          <Route path="/ipcs" element={<Navigate to="/ipc" replace />} />
          {/* TB-A: new IPC module routes */}
          <Route path="/ipc" element={isAuthenticated ? <IPCListPageV2 /> : <Navigate to="/login" replace />} />
          <Route path="/ipc/new" element={isAuthenticated ? <IPCCreatePageV2 /> : <Navigate to="/login" replace />} />
          <Route path="/ipc/:ipcId" element={isAuthenticated ? <IPCDetailPageV2 /> : <Navigate to="/login" replace />} />
          {/* === Wave 10 Agent F BEGIN === */}
          <Route path="/ipc-fidic" element={isAuthenticated ? <IPCFidicListPage /> : <Navigate to="/login" replace />} />
          <Route path="/ipc-fidic/new/:projectId" element={isAuthenticated ? <IPCFidicNewWizardPage /> : <Navigate to="/login" replace />} />
          <Route path="/ipc-fidic/:id" element={isAuthenticated ? <IPCFidicDetailPage /> : <Navigate to="/login" replace />} />
          {/* === Wave 10 Agent F END === */}
          <Route path="/schedule" element={isAuthenticated ? <SchedulePage /> : <Navigate to="/login" replace />} />
          <Route path="/portfolio" element={isAuthenticated ? <PortfolioPage /> : <Navigate to="/login" replace />} />
          <Route path="/portfolio/overhead" element={isAuthenticated ? <OverheadPoolPage /> : <Navigate to="/login" replace />} />
          <Route path="/" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />} />
          <Route path="/settings" element={isAuthenticated ? <SettingsPage /> : <Navigate to="/login" replace />} />
          <Route path="/approvals" element={isAuthenticated ? <ApprovalsInboxPage /> : <Navigate to="/login" replace />} />
          <Route path="/tenders" element={isAuthenticated ? <TendersPage /> : <Navigate to="/login" replace />} />
          <Route path="/tenders/:tenderId" element={isAuthenticated ? <TenderDetailsPage /> : <Navigate to="/login" replace />} />
          <Route path="/quality" element={<Navigate to="/quality/dashboard" replace />} />
          <Route path="/quality/dashboard" element={isAuthenticated ? <QualityDashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/quality/inspections" element={isAuthenticated ? <QualityInspectionsPage /> : <Navigate to="/login" replace />} />
          <Route path="/quality/inspections/:inspectionId" element={isAuthenticated ? <InspectionDetailsPage /> : <Navigate to="/login" replace />} />
          <Route path="/quality/ncrs" element={isAuthenticated ? <NCRsPage /> : <Navigate to="/login" replace />} />
          <Route path="/quality/ncrs/:ncrId" element={isAuthenticated ? <NCRDetailsPage /> : <Navigate to="/login" replace />} />
          <Route path="/quality/punch-list" element={isAuthenticated ? <PunchListPage /> : <Navigate to="/login" replace />} />
          {/* === Wave C / C3 (W2 route-dedup) — Variation Orders: canonical = /vo (fullest workflow: list/create/approve/history/detail). */}
          {/* Legacy /variation-orders/* (list+detail) and F4 /variations/* (list/detail/form) redirect to the /vo family. */}
          {/* ORDER MATTERS: the literal "/new" + "/create" redirects MUST precede the generic :id catch-all. */}
          <Route path="/variation-orders/new" element={<Navigate to="/vo/create" replace />} />
          <Route path="/variation-orders" element={<Navigate to="/vo" replace />} />
          <Route path="/variation-orders/:voId" element={<RedirectWithParams to="/vo/:voId" />} />
          {/* === Phase 2 Agent F4 (legacy /variations/*) — redirected to canonical /vo === */}
          <Route path="/variations/new" element={<Navigate to="/vo/create" replace />} />
          <Route path="/variations/:id/edit" element={<RedirectWithParams to="/vo/:id" />} />
          <Route path="/variations" element={<Navigate to="/vo" replace />} />
          <Route path="/variations/:id" element={<RedirectWithParams to="/vo/:id" />} />
          {/* === Phase 2 Agent F4 END === */}
          {/* Bare /boq has no page of its own -> send to the importer
              (previously matched no route and rendered a blank page). */}
          <Route path="/boq" element={<Navigate to="/boq/import" replace />} />
          <Route path="/boq/import" element={isAuthenticated ? <BOQImporterPage /> : <Navigate to="/login" replace />} />
          <Route path="/boq/import/premium" element={isAuthenticated ? <BOQImporterPremium /> : <Navigate to="/login" replace />} />
          {/* === Phase 2 Agent D1 BEGIN === */}
          {/* Fix B: the /boq/premium-import lane drove the removed
              /boq-importer/jobs backend. Redirect to the unified premium
              wizard (now on the canonical /boq/import path). */}
          <Route path="/boq/premium-import" element={<Navigate to="/boq/import/premium" replace />} />
          {/* === Phase 2 Agent D1 END === */}
          <Route path="/boq/import/templates" element={isAuthenticated ? <BOQTemplateManagerPage /> : <Navigate to="/login" replace />} />
          <Route path="/boq/import/:importId" element={isAuthenticated ? <BOQImportDetailsPage /> : <Navigate to="/login" replace />} />
          <Route path="/dms" element={isAuthenticated ? <DMSDashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/dms/drawings" element={isAuthenticated ? <DrawingsRegisterPage /> : <Navigate to="/login" replace />} />
          <Route path="/dms/drawings/:drawingId" element={isAuthenticated ? <DrawingDetailPage /> : <Navigate to="/login" replace />} />
          {/* TB-B BIM activation */}
          <Route path="/bim/library" element={isAuthenticated ? <BIMLibraryPage /> : <Navigate to="/login" replace />} />
          {/* === Wave 13 Agent BIM BEGIN === */}
          <Route path="/bim/import" element={isAuthenticated ? <Wave13BIMViewerPage /> : <Navigate to="/login" replace />} />
          <Route path="/bim/import/:modelId" element={isAuthenticated ? <Wave13BIMViewerPage /> : <Navigate to="/login" replace />} />
          <Route path="/bim/qto" element={isAuthenticated ? <Wave13BIMViewerPage /> : <Navigate to="/login" replace />} />
          {/* === Wave 13 Agent BIM END === */}
          <Route path="/dms/submittals" element={isAuthenticated ? <SubmittalsTrackerPage /> : <Navigate to="/login" replace />} />
          <Route path="/dms/submittals/:submittalId" element={isAuthenticated ? <SubmittalDetailPage /> : <Navigate to="/login" replace />} />
          <Route path="/dms/rfis" element={isAuthenticated ? <RFIInboxPage /> : <Navigate to="/login" replace />} />
          <Route path="/dms/rfis/:rfiId" element={isAuthenticated ? <RFIDetailPage /> : <Navigate to="/login" replace />} />
          <Route path="/dms/transmittals" element={isAuthenticated ? <TransmittalsListPage /> : <Navigate to="/login" replace />} />
          <Route path="/dms/transmittals/new" element={isAuthenticated ? <TransmittalCreatePage /> : <Navigate to="/login" replace />} />
          <Route path="/dms/transmittals/:transmittalId" element={isAuthenticated ? <TransmittalDetailPage /> : <Navigate to="/login" replace />} />
          <Route path="/hse" element={<Navigate to="/hse/dashboard" replace />} />
          <Route path="/hse/dashboard" element={isAuthenticated ? <HSEDashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/hse/permits" element={isAuthenticated ? <HSEPermitsPage /> : <Navigate to="/login" replace />} />
          <Route path="/hse/permits/:permitId" element={isAuthenticated ? <HSEPermitDetailsPage /> : <Navigate to="/login" replace />} />
          <Route path="/hse/toolbox-talks" element={isAuthenticated ? <HSEToolboxTalksPage /> : <Navigate to="/login" replace />} />
          <Route path="/hse/toolbox-talks/:tbtId" element={isAuthenticated ? <HSEToolboxTalkDetailsPage /> : <Navigate to="/login" replace />} />
          <Route path="/hse/incidents" element={isAuthenticated ? <HSEIncidentsPage /> : <Navigate to="/login" replace />} />
          <Route path="/hse/incidents/:incidentId" element={isAuthenticated ? <HSEIncidentDetailsPage /> : <Navigate to="/login" replace />} />
          <Route path="/hse/inspections" element={isAuthenticated ? <HSEInspectionsPage /> : <Navigate to="/login" replace />} />
          <Route path="/hse/inspections/:inspectionId" element={isAuthenticated ? <HSEInspectionDetailsPage /> : <Navigate to="/login" replace />} />
          <Route path="/hse/training" element={isAuthenticated ? <HSETrainingRecordsPage /> : <Navigate to="/login" replace />} />
          {/* === Phase 2 Wave 5 Agent M1 BEGIN === */}
          <Route path="/hse/wave5/dashboard" element={isAuthenticated ? <Wave5HSEDashboard /> : <Navigate to="/login" replace />} />
          <Route path="/hse/wave5/permits" element={isAuthenticated ? <Wave5HSEPermitsList /> : <Navigate to="/login" replace />} />
          <Route path="/hse/wave5/permits/:typeCode" element={isAuthenticated ? <Wave5HSEPermitDetail /> : <Navigate to="/login" replace />} />
          <Route path="/hse/wave5/incidents" element={isAuthenticated ? <Wave5HSEIncidents /> : <Navigate to="/login" replace />} />
          <Route path="/hse/wave5/near-misses" element={isAuthenticated ? <Wave5HSEIncidents /> : <Navigate to="/login" replace />} />
          <Route path="/hse/wave5/toolbox-talks" element={isAuthenticated ? <Wave5HSEToolboxTalks /> : <Navigate to="/login" replace />} />
          {/* === Phase 2 Wave 5 Agent M1 END === */}
          <Route path="/subcontractors" element={isAuthenticated ? <SubcontractorsListPage /> : <Navigate to="/login" replace />} />
          <Route path="/subcontractors/:subcontractorId" element={isAuthenticated ? <SubcontractorDetailPage /> : <Navigate to="/login" replace />} />
          <Route path="/subcontracts" element={isAuthenticated ? <SubcontractsListPage /> : <Navigate to="/login" replace />} />
          <Route path="/subcontracts/new" element={isAuthenticated ? <SubcontractCreatePage /> : <Navigate to="/login" replace />} />
          <Route path="/subcontracts/:contractId" element={isAuthenticated ? <SubcontractDetailPage /> : <Navigate to="/login" replace />} />
          <Route path="/subcontract-payments" element={isAuthenticated ? <SubcontractorPaymentsListPage /> : <Navigate to="/login" replace />} />
          <Route path="/subcontract-payments/new" element={isAuthenticated ? <SubcontractorPaymentCreatePage /> : <Navigate to="/login" replace />} />
          <Route path="/subcontract-payments/:certId" element={isAuthenticated ? <SubcontractorPaymentDetailPage /> : <Navigate to="/login" replace />} />
          {/* === Phase 2 Wave 6 Agent M2 (Subcontractor Management) BEGIN === */}
          <Route path="/subcontractors-v2" element={isAuthenticated ? <SubcontractorsListPageV2 /> : <Navigate to="/login" replace />} />
          <Route path="/subcontractors-v2/contracts" element={isAuthenticated ? <SubcontractorsContractsPageV2 /> : <Navigate to="/login" replace />} />
          <Route path="/subcontractors-v2/payments" element={isAuthenticated ? <SubcontractorsPaymentsPageV2 /> : <Navigate to="/login" replace />} />
          <Route path="/subcontractors-v2/:id" element={isAuthenticated ? <SubcontractorDetailPageV2 /> : <Navigate to="/login" replace />} />
          {/* === Phase 2 Wave 6 Agent M2 (Subcontractor Management) END === */}
          {/* === Wave C / C3 (W2 route-dedup) — Field: consolidate legacy /field/* under /field-app === */}
          {/* /field/reports list -> /field-app dashboard. The legacy quick-action paths
              (/field/reports/new, /field/photos/new) had NO real route and would have
              matched :reportId="new"; redirect them explicitly to the field-app forms.
              The per-report DETAIL surface has no field-app equivalent, so it is KEPT. */}
          <Route path="/field/reports/new" element={<Navigate to="/field-app/daily-report" replace />} />
          <Route path="/field/photos/new" element={<Navigate to="/field-app/photos/capture" replace />} />
          <Route path="/field/reports" element={<Navigate to="/field-app" replace />} />
          <Route path="/field/reports/:reportId" element={isAuthenticated ? <FieldReportDetailPage /> : <Navigate to="/login" replace />} />
          <Route path="/sync/pending" element={isAuthenticated ? <PendingSyncPage /> : <Navigate to="/login" replace />} />
          <Route path="/billing" element={<Navigate to="/billing/subscription" replace />} />
          <Route path="/billing/pricing" element={isAuthenticated ? <BillingPricingPage /> : <Navigate to="/login" replace />} />
          <Route path="/billing/subscription" element={isAuthenticated ? <BillingSubscriptionPage /> : <Navigate to="/login" replace />} />
          <Route path="/billing/success" element={isAuthenticated ? <BillingSuccessPage /> : <Navigate to="/login" replace />} />
          <Route path="/billing/cancel" element={isAuthenticated ? <BillingCancelPage /> : <Navigate to="/login" replace />} />
          {/* === Phase 2 Wave 5 Agent S1 BEGIN === */}
          <Route path="/settings/mfa" element={isAuthenticated ? <MFASettingsPage /> : <Navigate to="/login" replace />} />
          {/* === Phase 2 Wave 5 Agent S1 END === */}
          {/* === S4-P Email Signup + Billing (Sprint 4 Mega Wave 2) BEGIN === */}
          <Route path="/settings/billing" element={isAuthenticated ? <SettingsBillingPage /> : <Navigate to="/login" replace />} />
          <Route path="/settings/subscription" element={isAuthenticated ? <SettingsSubscriptionPage /> : <Navigate to="/login" replace />} />
          <Route path="/settings/usage" element={isAuthenticated ? <SettingsUsagePage /> : <Navigate to="/login" replace />} />
          {/* === S4-P Email Signup + Billing (Sprint 4 Mega Wave 2) END === */}
          {/* === Phase 2 Wave 6 Agent S3 (Password Policy + Session Management) BEGIN === */}
          <Route path="/settings/password-policy" element={isAuthenticated ? <PasswordPolicyAdminPage /> : <Navigate to="/login" replace />} />
          {/* === Phase 2 Wave 6 Agent S3 (Password Policy + Session Management) END === */}
          {/* === Wave 11 Agent CUR BEGIN === */}
          <Route path="/settings/currency" element={isAuthenticated ? <Wave11CurrencySettingsPage /> : <Navigate to="/login" replace />} />
          <Route path="/settings/hijri" element={isAuthenticated ? <Wave11HijriSettingsPage /> : <Navigate to="/login" replace />} />
          {/* === Wave 11 Agent CUR END === */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/verify-email-pending" element={isAuthenticated ? <VerifyPendingPage /> : <Navigate to="/login" replace />} />
          <Route path="/invoice-scans" element={isAuthenticated ? <InvoiceScansListPage /> : <Navigate to="/login" replace />} />
          <Route path="/invoice-scans/stats" element={isAuthenticated ? <InvoiceScanStatsPage /> : <Navigate to="/login" replace />} />
          <Route path="/invoice-scans/:id" element={isAuthenticated ? <InvoiceScanReviewPage /> : <Navigate to="/login" replace />} />
          <Route path="/tax/vat/returns" element={isAuthenticated ? <VATReturnsListPage /> : <Navigate to="/login" replace />} />
          <Route path="/tax/vat/returns/:returnId" element={isAuthenticated ? <VATReturnDetailPage /> : <Navigate to="/login" replace />} />
          <Route path="/tax/ct/returns" element={isAuthenticated ? <CTReturnsListPage /> : <Navigate to="/login" replace />} />
          <Route path="/tax/ct/returns/:returnId" element={isAuthenticated ? <CTReturnDetailPage /> : <Navigate to="/login" replace />} />
          <Route path="/tax/config" element={isAuthenticated ? <VATConfigPage /> : <Navigate to="/login" replace />} />
          <Route path="/workforce" element={<Navigate to="/workforce/employees" replace />} />
          <Route path="/workforce/employees" element={isAuthenticated ? <EmployeesListPage /> : <Navigate to="/login" replace />} />
          <Route path="/workforce/employees/:employeeId" element={isAuthenticated ? <EmployeeDetailPage /> : <Navigate to="/login" replace />} />
          <Route path="/workforce/departments" element={isAuthenticated ? <DepartmentsTreePage /> : <Navigate to="/login" replace />} />
          <Route path="/workforce/leave" element={isAuthenticated ? <LeaveRequestsPage /> : <Navigate to="/login" replace />} />
          <Route path="/workforce/attendance" element={isAuthenticated ? <AttendanceDashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/payroll" element={<Navigate to="/payroll/periods" replace />} />
          <Route path="/payroll/periods" element={isAuthenticated ? <PayrollPeriodsPage /> : <Navigate to="/login" replace />} />
          <Route path="/payroll/periods/:periodId" element={isAuthenticated ? <PayrollRunWizardPage /> : <Navigate to="/login" replace />} />
          <Route path="/payroll/payslips/:payslipId" element={isAuthenticated ? <PayslipDetailPage /> : <Navigate to="/login" replace />} />
          <Route path="/payroll/runs/:runId/wps" element={isAuthenticated ? <WPSExportPage /> : <Navigate to="/login" replace />} />
          <Route path="/payroll/gratuity" element={isAuthenticated ? <GratuityCalculatorPage /> : <Navigate to="/login" replace />} />
          {/* === Wave 11 Agent HR BEGIN === */}
          <Route path="/workforce/dashboard" element={isAuthenticated ? <WorkforceDashboard /> : <Navigate to="/login" replace />} />
          <Route path="/workforce/anti-ghost" element={isAuthenticated ? <AntiGhostWorkerPage /> : <Navigate to="/login" replace />} />
          <Route path="/workforce/project-cost" element={isAuthenticated ? <ProjectLaborCostPage /> : <Navigate to="/login" replace />} />
          <Route path="/workforce/productivity" element={isAuthenticated ? <WorkforceProductivityPage /> : <Navigate to="/login" replace />} />
          <Route path="/workforce/payroll" element={isAuthenticated ? <WorkforcePayrollPage /> : <Navigate to="/login" replace />} />
          <Route path="/workforce/wps" element={isAuthenticated ? <WPSEnterprisePage /> : <Navigate to="/login" replace />} />
          {/* === Wave 11 Agent HR END === */}
          {/* === AGENT_H (Frontend Set 1) BEGIN === */}
          <Route path="/predictive" element={isAuthenticated ? <PredictiveDashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/predictive/alerts" element={isAuthenticated ? <PredictiveAlertsPage /> : <Navigate to="/login" replace />} />
          <Route path="/predictive/projects/:projectId" element={isAuthenticated ? <PredictiveProjectPage /> : <Navigate to="/login" replace />} />
          <Route path="/predictive/projects/:projectId/history" element={isAuthenticated ? <PredictiveHistoryPage /> : <Navigate to="/login" replace />} />
          {/* === AGENT_S4D (S4-D AI Predictive Project Analytics) BEGIN === */}
          <Route path="/predictive/v2" element={isAuthenticated ? <PredictiveV2DashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/predictive/v2/projects/:projectId" element={isAuthenticated ? <PredictiveV2ProjectPage /> : <Navigate to="/login" replace />} />
          <Route path="/predictive/v2/projects/:projectId/scenario" element={isAuthenticated ? <PredictiveV2ScenarioPage /> : <Navigate to="/login" replace />} />
          {/* === AGENT_S4D (S4-D AI Predictive Project Analytics) END === */}
          <Route path="/locale-settings" element={isAuthenticated ? <LocaleSettingsPage /> : <Navigate to="/login" replace />} />
          <Route path="/hijri" element={isAuthenticated ? <HijriCalendarPage /> : <Navigate to="/login" replace />} />
          <Route path="/currency-conversions" element={isAuthenticated ? <CurrencyConversionsPage /> : <Navigate to="/login" replace />} />
          <Route path="/chat" element={isAuthenticated ? <ChatSessionsPage /> : <Navigate to="/login" replace />} />
          <Route path="/chat/sessions/:sessionId" element={isAuthenticated ? <ChatSessionPage /> : <Navigate to="/login" replace />} />
          {/* === Wave 10 Agent A BEGIN === */}
          <Route path="/chatbot" element={isAuthenticated ? <Wave10AChatbotPage /> : <Navigate to="/login" replace />} />
          <Route path="/project/:projectId/chatbot" element={isAuthenticated ? <Wave10AChatbotPage /> : <Navigate to="/login" replace />} />
          {/* === Wave 10 Agent A END === */}
          {/* === AGENT_H END === */}
          {/* === AGENT_I (Frontend Set 2) BEGIN === */}
          <Route path="/boq/wizard" element={isAuthenticated ? <BOQWizardStartPage /> : <Navigate to="/login" replace />} />
          <Route path="/boq/wizard/:wizardId/upload" element={isAuthenticated ? <BOQWizardUploadPage /> : <Navigate to="/login" replace />} />
          <Route path="/boq/wizard/:wizardId/template" element={isAuthenticated ? <BOQWizardTemplatePage /> : <Navigate to="/login" replace />} />
          <Route path="/boq/wizard/:wizardId/mapping" element={isAuthenticated ? <BOQWizardMappingPage /> : <Navigate to="/login" replace />} />
          <Route path="/boq/wizard/:wizardId/preview" element={isAuthenticated ? <BOQWizardPreviewPage /> : <Navigate to="/login" replace />} />
          <Route path="/boq/wizard/:wizardId/apply" element={isAuthenticated ? <BOQWizardApplyPage /> : <Navigate to="/login" replace />} />
          <Route path="/dms/letters" element={isAuthenticated ? <DMSLettersInboxPage /> : <Navigate to="/login" replace />} />
          <Route path="/dms/letters/new" element={isAuthenticated ? <DMSLetterComposePage /> : <Navigate to="/login" replace />} />
          <Route path="/dms/letters/:letterId" element={isAuthenticated ? <DMSLetterDetailPage /> : <Navigate to="/login" replace />} />
          <Route path="/security" element={<Navigate to="/security/dashboard" replace />} />
          <Route path="/security/dashboard" element={isAuthenticated ? <RBACSecurityDashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/security/2fa" element={isAuthenticated ? <RBAC2FASetupPage /> : <Navigate to="/login" replace />} />
          <Route path="/security/audit-log" element={isAuthenticated ? <RBACAuditLogPage /> : <Navigate to="/login" replace />} />
          <Route path="/security/sessions" element={isAuthenticated ? <RBACSessionsPage /> : <Navigate to="/login" replace />} />
          <Route path="/security/policies" element={isAuthenticated ? <RBACPoliciesPage /> : <Navigate to="/login" replace />} />
          <Route path="/security/policies/:policyId" element={isAuthenticated ? <RBACPolicyDetailPage /> : <Navigate to="/login" replace />} />
          <Route path="/security/ai/suggestions" element={isAuthenticated ? <RBACAISuggestionsPage /> : <Navigate to="/login" replace />} />
          <Route path="/security/ai/anomalies" element={isAuthenticated ? <RBACAnomaliesPage /> : <Navigate to="/login" replace />} />
          <Route path="/materials" element={<Navigate to="/materials/catalog" replace />} />
          <Route path="/materials/catalog" element={isAuthenticated ? <MaterialCatalogPage /> : <Navigate to="/login" replace />} />
          <Route path="/materials/requests" element={isAuthenticated ? <MaterialRequestsPage /> : <Navigate to="/login" replace />} />
          <Route path="/materials/consumption" element={isAuthenticated ? <MaterialConsumptionPage /> : <Navigate to="/login" replace />} />
          <Route path="/materials/theft-alerts" element={isAuthenticated ? <MaterialTheftAlertsPage /> : <Navigate to="/login" replace />} />
          {/* === Wave 11 Agent MAT BEGIN === */}
          <Route path="/materials/lifecycle/requests" element={isAuthenticated ? <Wave11MatRequestsPage /> : <Navigate to="/login" replace />} />
          <Route path="/materials/lifecycle/requests/new" element={isAuthenticated ? <Wave11MatRequestFormPage /> : <Navigate to="/login" replace />} />
          <Route path="/materials/lifecycle/requests/:id" element={isAuthenticated ? <Wave11MatRequestDetailPage /> : <Navigate to="/login" replace />} />
          <Route path="/materials/lifecycle/theft-alerts" element={isAuthenticated ? <Wave11MatTheftAlertsPage /> : <Navigate to="/login" replace />} />
          <Route path="/materials/lifecycle/reconciliation" element={isAuthenticated ? <Wave11MatReconciliationPage /> : <Navigate to="/login" replace />} />
          {/* === Wave 11 Agent MAT END === */}
          <Route path="/sustainability" element={isAuthenticated ? <SustainabilityDashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/sustainability/certifications" element={isAuthenticated ? <SustainabilityCertificationsPage /> : <Navigate to="/login" replace />} />
          <Route path="/sustainability/carbon" element={isAuthenticated ? <SustainabilityCarbonPage /> : <Navigate to="/login" replace />} />
          <Route path="/sustainability/resources" element={isAuthenticated ? <SustainabilityResourcesPage /> : <Navigate to="/login" replace />} />
          {/* === AGENT_S4_M (Sustainability v2) BEGIN === */}
          <Route path="/sustainability/v2" element={isAuthenticated ? <SustainabilityV2DashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/sustainability/v2/estidama" element={isAuthenticated ? <SustainabilityV2EstidamaPage /> : <Navigate to="/login" replace />} />
          <Route path="/sustainability/v2/leed" element={isAuthenticated ? <SustainabilityV2LEEDPage /> : <Navigate to="/login" replace />} />
          <Route path="/sustainability/v2/carbon" element={isAuthenticated ? <SustainabilityV2CarbonPage /> : <Navigate to="/login" replace />} />
          {/* === AGENT_S4_M (Sustainability v2) END === */}
          {/* === Wave 8/9 Agent M11 BEGIN === */}
          <Route path="/sustainability/m11/:projectId" element={isAuthenticated ? <SustainabilityM11Dashboard /> : <Navigate to="/login" replace />} />
          <Route path="/sustainability/m11/:projectId/estidama" element={isAuthenticated ? <SustainabilityM11Estidama /> : <Navigate to="/login" replace />} />
          <Route path="/sustainability/m11/:projectId/leed" element={isAuthenticated ? <SustainabilityM11LEED /> : <Navigate to="/login" replace />} />
          <Route path="/sustainability/m11/:projectId/carbon" element={isAuthenticated ? <SustainabilityM11Carbon /> : <Navigate to="/login" replace />} />
          {/* === Wave 8/9 Agent M11 END === */}
          {/* === Wave 10 Agent G (Project Health Dashboard) BEGIN === */}
          <Route path="/health/portfolio" element={isAuthenticated ? <PortfolioOverviewPage /> : <Navigate to="/login" replace />} />
          <Route path="/health/project/:id" element={isAuthenticated ? <HealthDashboardPage /> : <Navigate to="/login" replace />} />
          {/* === Wave 10 Agent G (Project Health Dashboard) END === */}
          {/* === AGENT_I (Frontend Set 2) END === */}
          {/* === AGENT_J (Frontend Set 3) BEGIN === */}
          <Route path="/cost-estimation" element={isAuthenticated ? <CostEstimationListPage /> : <Navigate to="/login" replace />} />
          <Route path="/cost-estimation/new" element={isAuthenticated ? <CostEstimationNewPage /> : <Navigate to="/login" replace />} />
          <Route path="/cost-estimation/historical-rates" element={isAuthenticated ? <CostHistoricalRatesPage /> : <Navigate to="/login" replace />} />
          <Route path="/cost-estimation/benchmarks" element={isAuthenticated ? <CostBenchmarksPage /> : <Navigate to="/login" replace />} />
          <Route path="/cost-estimation/:estimateId" element={isAuthenticated ? <CostEstimationDetailsPage /> : <Navigate to="/login" replace />} />
          {/* === Phase 2 Agent F2 BEGIN === */}
          <Route path="/estimation/ai" element={isAuthenticated ? <AICostEstimationPage /> : <Navigate to="/login" replace />} />
          {/* === Phase 2 Agent F2 END === */}
          {/* === Phase 2 Wave 5 Agent E3 BEGIN === */}
          <Route path="/analytics/estimation" element={isAuthenticated ? <EstimationAnalyticsPage /> : <Navigate to="/login" replace />} />
          {/* === Phase 2 Wave 5 Agent E3 END === */}
          <Route path="/qto" element={<Navigate to="/qto/drawings" replace />} />
          <Route path="/qto/drawings" element={isAuthenticated ? <QTODrawingsPage /> : <Navigate to="/login" replace />} />
          <Route path="/qto/drawings/:drawingId" element={isAuthenticated ? <QTODrawingViewerPage /> : <Navigate to="/login" replace />} />
          <Route path="/qto/drawings/:drawingId/canvas" element={isAuthenticated ? <QTOCanvasMeasurePage /> : <Navigate to="/login" replace />} />
          <Route path="/qto/measurements" element={isAuthenticated ? <QTOMeasurementsPage /> : <Navigate to="/login" replace />} />
          <Route path="/qto/reports" element={isAuthenticated ? <QTOReportsPage /> : <Navigate to="/login" replace />} />
          {/* === STAGE3-S6-BOQ-MAPPING BEGIN === */}
          <Route path="/qto/boq-mapping" element={isAuthenticated ? <BoqMappingPage /> : <Navigate to="/login" replace />} />
          {/* === STAGE3-S6-BOQ-MAPPING END === */}
          {/* === STAGE4-P1E-MAPPING-UI-START === */}
          <Route path="/projects/:projectId/boq-mapping" element={isAuthenticated ? <BoqMappingPage /> : <Navigate to="/login" replace />} />
          {/* === STAGE4-P1E-MAPPING-UI-END === */}
          {/* === STAGE4-P2E (Pricing UI) BEGIN === */}
          {/* PRICING-API-CONTRACT-01: the pricing dashboard is UNROUTED.
              GET /pricing/dashboard/{projectId} has no server route, so this
              page could only ever render its error state. The page code is
              kept (see pages/PricingDashboardPage.tsx) and rebuilding it on
              real data is backlog PRICING-DASHBOARD-01, after E2/E4 -- today
              it would render emptiness even WITH an endpoint, because
              boq_rate_history, pricing_rates and item_catalog are all 0 on
              prod. Re-add this line when there is something to show. */}
          {/* === STAGE4-P2E END === */}
          {/* === STAGE4-P5B (One-Click Generate UI) BEGIN === */}
          <Route path="/qto/generate" element={isAuthenticated ? <QTOGenerateWizardPage /> : <Navigate to="/login" replace />} />
          {/* === STAGE4-P5B END === */}
          <Route path="/govt" element={isAuthenticated ? <GovtIntegrationsDashboard /> : <Navigate to="/login" replace />} />
          <Route path="/govt/mol" element={isAuthenticated ? <MOLLaborCompliancePage /> : <Navigate to="/login" replace />} />
          <Route path="/govt/wps" element={isAuthenticated ? <WPSEnhancedPage /> : <Navigate to="/login" replace />} />
          <Route path="/govt/permits" element={isAuthenticated ? <MunicipalityPermitsPage /> : <Navigate to="/login" replace />} />
          <Route path="/govt/eida" element={isAuthenticated ? <EmiratesIDVerificationPage /> : <Navigate to="/login" replace />} />
          <Route path="/govt/fta" element={isAuthenticated ? <TaxAuthorityFilingPage /> : <Navigate to="/login" replace />} />
          {/* === AGENT_J (Frontend Set 3) END === */}
          {/* === RUN10-FE-2 (Tender v2 + VO v2) BEGIN === */}
          <Route path="/tender" element={isAuthenticated ? <Run10TenderListPage /> : <Navigate to="/login" replace />} />
          <Route path="/tender/create" element={isAuthenticated ? <Run10TenderCreatePage /> : <Navigate to="/login" replace />} />
          <Route path="/tender/:tenderId/evaluate" element={isAuthenticated ? <Run10TenderEvaluatePage /> : <Navigate to="/login" replace />} />
          <Route path="/tender/:tenderId/award" element={isAuthenticated ? <Run10TenderAwardPage /> : <Navigate to="/login" replace />} />
          <Route path="/vo" element={isAuthenticated ? <Run10VOListPage /> : <Navigate to="/login" replace />} />
          <Route path="/vo/create" element={isAuthenticated ? <Run10VOCreatePage /> : <Navigate to="/login" replace />} />
          <Route path="/vo/:voId/approve" element={isAuthenticated ? <Run10VOApprovePage /> : <Navigate to="/login" replace />} />
          <Route path="/vo/:voId/history" element={isAuthenticated ? <Run10VOHistoryPage /> : <Navigate to="/login" replace />} />
          {/* === RUN10-FE-2 END === */}
          {/* === Agent W2-K start === */}
          <Route path="/vo/:voId" element={isAuthenticated ? <W2KVODetailPage /> : <Navigate to="/login" replace />} />
          {/* === Agent W2-K end === */}
          {/* === AGENT_W3_L (Tender Bidding) BEGIN === */}
          <Route path="/bidding" element={isAuthenticated ? <TenderBiddingDashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/bidding/strategy" element={isAuthenticated ? <TenderBiddingStrategyPage /> : <Navigate to="/login" replace />} />
          <Route path="/bidding/bonds" element={isAuthenticated ? <TenderBiddingBondsPage /> : <Navigate to="/login" replace />} />
          <Route path="/bidding/prequalifications" element={isAuthenticated ? <TenderBiddingPQPage /> : <Navigate to="/login" replace />} />
          {/* === AGENT_W3_L (Tender Bidding) END === */}
          {/* === AGENT_FE-3 (Run #10 — QA/QC + Set 3 polish) BEGIN === */}
          <Route path="/qa-qc" element={<Navigate to="/qa-qc/inspections" replace />} />
          <Route path="/qa-qc/inspections" element={isAuthenticated ? <QAQCInspectionsPage /> : <Navigate to="/login" replace />} />
          <Route path="/qa-qc/checklists" element={isAuthenticated ? <QAQCChecklistsPage /> : <Navigate to="/login" replace />} />
          <Route path="/qa-qc/ncrs" element={isAuthenticated ? <QAQCNCRsPage /> : <Navigate to="/login" replace />} />
          <Route path="/qa-qc/audits" element={isAuthenticated ? <QAQCAuditsPage /> : <Navigate to="/login" replace />} />
          <Route path="/qa-qc/reports" element={isAuthenticated ? <QAQCReportsPage /> : <Navigate to="/login" replace />} />
          <Route path="/qa-qc/settings" element={isAuthenticated ? <QAQCSettingsPage /> : <Navigate to="/login" replace />} />
          <Route path="/qto/upload" element={isAuthenticated ? <QTODrawingUploadPage /> : <Navigate to="/login" replace />} />
          {/* === Phase 2 Agent F1 BEGIN === */}
          <Route path="/qto/auto-mapping/:projectId" element={isAuthenticated ? <QTOAutoMappingPage /> : <Navigate to="/login" replace />} />
          {/* === Phase 2 Agent F1 END === */}
          {/* === QTO Package Intake Preview BEGIN === */}
          <Route path="/qto/package-intake" element={isAuthenticated ? <QTOPackageIntakePreviewPage /> : <Navigate to="/login" replace />} />
          {/* === QTO Package Intake Preview END === */}
          <Route path="/cost-estimation/comparison" element={isAuthenticated ? <CostEstimationComparisonPage /> : <Navigate to="/login" replace />} />
          <Route path="/govt/compliance" element={isAuthenticated ? <GovtComplianceDashboardPage /> : <Navigate to="/login" replace />} />
          {/* === AGENT_S4_C (Sprint 4 -- UAE Government Integration MVP) BEGIN === */}
          <Route path="/government" element={isAuthenticated ? <GovernmentOverviewPage /> : <Navigate to="/login" replace />} />
          <Route path="/government/mohre" element={isAuthenticated ? <GovernmentMoHREPage /> : <Navigate to="/login" replace />} />
          <Route path="/government/wps" element={isAuthenticated ? <GovernmentWPSPage /> : <Navigate to="/login" replace />} />
          <Route path="/government/emaratax" element={isAuthenticated ? <GovernmentEmaraTaxPage /> : <Navigate to="/login" replace />} />
          <Route path="/government/municipality" element={isAuthenticated ? <GovernmentMunicipalityPage /> : <Navigate to="/login" replace />} />
          {/* === AGENT_S4_C (Sprint 4 -- UAE Government Integration MVP) END === */}
          {/* === AGENT_FE-3 (Run #10) END === */}
          {/* === AGENT_S4_G (QA/QC Phase 2 FE) BEGIN === */}
          <Route path="/qaqc" element={isAuthenticated ? <QAQCDashboardPageNew /> : <Navigate to="/login" replace />} />
          <Route path="/qaqc/itps" element={isAuthenticated ? <QAQCITPsPage /> : <Navigate to="/login" replace />} />
          <Route path="/qaqc/ncrs" element={isAuthenticated ? <QAQCNCRsPageNew /> : <Navigate to="/login" replace />} />
          <Route path="/qaqc/punch-lists" element={isAuthenticated ? <QAQCPunchListsPage /> : <Navigate to="/login" replace />} />
          <Route path="/qaqc/material-testing" element={isAuthenticated ? <QAQCMaterialTestingPageNew /> : <Navigate to="/login" replace />} />
          <Route path="/qaqc/concrete-pours" element={isAuthenticated ? <QAQCConcretePourLogPage /> : <Navigate to="/login" replace />} />
          <Route path="/qaqc/compliance-reports" element={isAuthenticated ? <QAQCComplianceReportsPage /> : <Navigate to="/login" replace />} />
          {/* === Wave 11 Agent QC2 BEGIN === */}
          <Route path="/qaqc/extensions/material-tests" element={isAuthenticated ? <Wave11MaterialTestsPage /> : <Navigate to="/login" replace />} />
          <Route path="/qaqc/extensions/cube-tests" element={isAuthenticated ? <Wave11CubeTestsPage /> : <Navigate to="/login" replace />} />
          <Route path="/qaqc/extensions/slump-tests" element={isAuthenticated ? <Wave11SlumpTestsPage /> : <Navigate to="/login" replace />} />
          <Route path="/qaqc/extensions/concrete-pours" element={isAuthenticated ? <Wave11ConcretePourLogPage /> : <Navigate to="/login" replace />} />
          <Route path="/qaqc/extensions/mix-designs" element={isAuthenticated ? <Wave11MixDesignLibraryPage /> : <Navigate to="/login" replace />} />
          {/* === Wave 11 Agent QC2 END === */}
          {/* === AGENT_S4_G (QA/QC Phase 2 FE) END === */}
          {/* === AGENT_FE-1 (Run #10 — Client Portal) BEGIN === */}
          {/* Portal routes are registered once below, BrandingProvider-wrapped (S4-N polished). */}
          {/* Only the /portal index redirect lives here; per-page routes are the branded block. */}
          <Route path="/portal" element={<Navigate to="/portal/dashboard" replace />} />
          {/* === Wave 10 Agent D BEGIN === */}
          <Route path="/portal/mobile" element={<PortalProtectedRoute><Wave10DMobileDashboard /></PortalProtectedRoute>} />
          <Route path="/portal/approvals/v2" element={<PortalProtectedRoute><Wave10DApprovalsPage /></PortalProtectedRoute>} />
          <Route path="/portal/notifications" element={<PortalProtectedRoute><Wave10DNotificationsPage /></PortalProtectedRoute>} />
          {/* === Wave 10 Agent D END === */}

          {/* === AGENT_S4H (Asset / Equipment frontend) BEGIN === */}
          <Route path="/equipment" element={isAuthenticated ? <EquipmentListPage /> : <Navigate to="/login" replace />} />
          <Route path="/equipment/gps-map" element={isAuthenticated ? <EquipmentGPSMapPage /> : <Navigate to="/login" replace />} />
          <Route path="/equipment/maintenance" element={isAuthenticated ? <EquipmentMaintenanceSchedulePage /> : <Navigate to="/login" replace />} />
          <Route path="/equipment/fuel" element={isAuthenticated ? <EquipmentFuelTrackingPage /> : <Navigate to="/login" replace />} />
          <Route path="/equipment/cost-allocation" element={isAuthenticated ? <EquipmentCostAllocationPage /> : <Navigate to="/login" replace />} />
          <Route path="/equipment/:id" element={isAuthenticated ? <EquipmentDetailPage /> : <Navigate to="/login" replace />} />
          {/* === AGENT_S4H (Asset / Equipment frontend) END === */}
          <Route
            path="/portal/login"
            element={
              <BrandingProvider>
                <PortalLoginPage />
              </BrandingProvider>
            }
          />
          <Route
            path="/portal/dashboard"
            element={
              <PortalProtectedRoute>
                <BrandingProvider authenticated>
                  <PortalDashboardPage />
                </BrandingProvider>
              </PortalProtectedRoute>
            }
          />
          <Route
            path="/portal/approvals"
            element={
              <PortalProtectedRoute>
                <BrandingProvider authenticated>
                  <PortalApprovalsPage />
                </BrandingProvider>
              </PortalProtectedRoute>
            }
          />
          <Route
            path="/portal/rfis"
            element={
              <PortalProtectedRoute>
                <BrandingProvider authenticated>
                  <PortalRFIsPage />
                </BrandingProvider>
              </PortalProtectedRoute>
            }
          />
          <Route
            path="/portal/progress"
            element={
              <PortalProtectedRoute>
                <BrandingProvider authenticated>
                  <PortalProgressPage />
                </BrandingProvider>
              </PortalProtectedRoute>
            }
          />
          <Route
            path="/portal/documents"
            element={
              <PortalProtectedRoute>
                <BrandingProvider authenticated>
                  <PortalDocumentsPage />
                </BrandingProvider>
              </PortalProtectedRoute>
            }
          />
          <Route
            path="/portal/reports"
            element={
              <PortalProtectedRoute>
                <BrandingProvider authenticated>
                  <PortalReportsPage />
                </BrandingProvider>
              </PortalProtectedRoute>
            }
          />
          <Route
            path="/portal/settings"
            element={
              <PortalProtectedRoute>
                <BrandingProvider authenticated>
                  <PortalSettingsPage />
                </BrandingProvider>
              </PortalProtectedRoute>
            }
          />
          {/* === AGENT_FE-1 (Run #10 — Client Portal) END === */}
          {/* === NAV_V2 (Group Route Aliases) === */}
          {renderGroupRouteAliases(isAuthenticated)}
          {renderProjectRouteAliases(isAuthenticated)}
          {/* === NAV_V2 END === */}
{/* Sprint 5 - Tender QTO Engine */}
<Route 
  path="/tenders/:tenderId/qto" 
  element={isAuthenticated ? <TenderQTOPage /> : <Navigate to="/login" />} 
/>
{/* TAKEOFF-CONFIRM-PANEL-01 — param name `tenderId` matches useParams in the page */}
<Route
  path="/tenders/:tenderId/qto/takeoff-confirm"
  element={isAuthenticated ? <TenderTakeoffConfirmPage /> : <Navigate to="/login" />}
/>
<Route 
  path="/tenders/:tenderId/estimation" 
  element={isAuthenticated ? <TenderEstimationPage /> : <Navigate to="/login" />} 
/>
<Route 
  path="/tenders/:tenderId/estimation/:estimateId" 
  element={isAuthenticated ? <TenderEstimationPage /> : <Navigate to="/login" />} 
/>
<Route 
  path="/group/items/catalog" 
  element={isAuthenticated ? <ItemCatalogPage /> : <Navigate to="/login" />} 
/>
          {/* === Agent S4-AA (CRM-lite) BEGIN === */}
          <Route path="/crm/leads"
                 element={isAuthenticated ? <CRMLeadsPage /> : <Navigate to="/login" replace />} />
          <Route path="/crm/leads/:leadId"
                 element={isAuthenticated ? <CRMLeadDetailPage /> : <Navigate to="/login" replace />} />
          <Route path="/crm/pipeline"
                 element={isAuthenticated ? <CRMPipelinePage /> : <Navigate to="/login" replace />} />
          <Route path="/crm/campaigns"
                 element={isAuthenticated ? <CRMCampaignsPage /> : <Navigate to="/login" replace />} />
          <Route path="/crm/campaigns/new"
                 element={isAuthenticated ? <CRMCampaignBuilderPage /> : <Navigate to="/login" replace />} />
          <Route path="/crm/analytics"
                 element={isAuthenticated ? <CRMAnalyticsPage /> : <Navigate to="/login" replace />} />
          {/* === Agent S4-AA (CRM-lite) END === */}

          {/* === Phase 2 Wave 7 Agent M7 BEGIN === */}
          <Route
            path="/dms-premium"
            element={isAuthenticated ? <DMSPremiumDashboard /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/dms-premium/drawings"
            element={isAuthenticated ? <DMSPremiumDrawings /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/dms-premium/submittals"
            element={isAuthenticated ? <DMSPremiumSubmittals /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/dms-premium/submittals/:id"
            element={isAuthenticated ? <DMSPremiumSubmittalDetail /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/dms-premium/rfis"
            element={isAuthenticated ? <DMSPremiumRFIs /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/dms-premium/rfis/:id"
            element={isAuthenticated ? <DMSPremiumRFIDetail /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/dms-premium/transmittals"
            element={isAuthenticated ? <DMSPremiumTransmittals /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/dms-premium/transmittals/new"
            element={isAuthenticated ? <DMSPremiumTransmittalNew /> : <Navigate to="/login" replace />}
          />
          {/* === Phase 2 Wave 7 Agent M7 END === */}

          {/* === Wave 11 Agent FIELD BEGIN === */}
          <Route
            path="/field-app"
            element={isAuthenticated ? <FieldAppDashboard /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/field-app/daily-report"
            element={isAuthenticated ? <FieldAppDailyReport /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/field-app/photos/capture"
            element={isAuthenticated ? <FieldAppPhotoCapture /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/field-app/voice"
            element={isAuthenticated ? <FieldAppVoiceNote /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/field-app/photos"
            element={isAuthenticated ? <FieldAppPhotoGallery /> : <Navigate to="/login" replace />}
          />
          {/* === Wave 11 Agent FIELD END === */}

          {/* === Wave 14 Agent POLISH BEGIN === */}
          {/* Procurement Hub (Wave 10 H) */}
          <Route path="/procurement" element={isAuthenticated ? <W14ProcurementDashboard /> : <Navigate to="/login" replace />} />
          {/* C3 W2 route-dedup: /procurement/suppliers is INTENTIONALLY kept distinct from the
              /suppliers directory — it is a separate procurement feature (procurementApi:
              ratings, document-expiry, eligibility), NOT a duplicate. Canonical directory
              page stays /suppliers (+ /suppliers/:supplierName). No redirect here by design. */}
          <Route path="/procurement/suppliers" element={isAuthenticated ? <W14ProcurementSuppliers /> : <Navigate to="/login" replace />} />
          {/* RFQ routes (list / create / detail) come from a shared builder so
              App.tsx and the route tests consume the SAME declarations
              (PROC-ROUTES-BUILDER-01). `new` is declared before `:rfqId` there. */}
          {buildProcurementRoutes(isAuthenticated)}
          <Route path="/procurement/pos" element={isAuthenticated ? <W14ProcurementPOs /> : <Navigate to="/login" replace />} />
          <Route path="/procurement/grns" element={isAuthenticated ? <W14ProcurementGRNs /> : <Navigate to="/login" replace />} />
          <Route path="/procurement/analytics" element={isAuthenticated ? <W14ProcurementAnalytics /> : <Navigate to="/login" replace />} />
          {/* RBAC Premium admin */}
          <Route path="/admin/rbac-premium" element={isAuthenticated ? <W14RBACPremium /> : <Navigate to="/login" replace />} />
          <Route path="/admin/role-builder" element={isAuthenticated ? <W14RBACRoleBuilder /> : <Navigate to="/login" replace />} />
          <Route path="/admin/access-anomalies" element={isAuthenticated ? <W14RBACAccessAnomalies /> : <Navigate to="/login" replace />} />
          <Route path="/admin/approval-workflows" element={isAuthenticated ? <W14RBACApprovalWorkflows /> : <Navigate to="/login" replace />} />
          {/* Analytics premium */}
          <Route path="/analytics/benchmarks" element={isAuthenticated ? <W14IndustryBenchmarks /> : <Navigate to="/login" replace />} />
          {/* BOQ Revisions */}
          <Route path="/boq/revisions" element={isAuthenticated ? <W14BOQRevisions /> : <Navigate to="/login" replace />} />
          {/* Cost Comparison + Alerts */}
          <Route path="/cost-comparison" element={isAuthenticated ? <W14CostComparison /> : <Navigate to="/login" replace />} />
          <Route path="/cost-alerts" element={isAuthenticated ? <W14CostAlerts /> : <Navigate to="/login" replace />} />
          {/* Estimation premium */}
          <Route path="/estimation/risk" element={isAuthenticated ? <W14EstRiskAssessment /> : <Navigate to="/login" replace />} />
          <Route path="/estimation/scenarios" element={isAuthenticated ? <W14EstScenarios /> : <Navigate to="/login" replace />} />
          <Route path="/estimation/templates" element={isAuthenticated ? <W14EstTemplates /> : <Navigate to="/login" replace />} />
          {/* Tax premium */}
          <Route path="/tax/dashboard" element={isAuthenticated ? <W14TaxComplianceDashboard /> : <Navigate to="/login" replace />} />
          <Route path="/tax/vat" element={isAuthenticated ? <W14TaxVAT /> : <Navigate to="/login" replace />} />
          <Route path="/tax/ct" element={isAuthenticated ? <W14TaxCT /> : <Navigate to="/login" replace />} />
          <Route path="/tax/owner-vat-refund" element={isAuthenticated ? <W14TaxOwnerVATRefund /> : <Navigate to="/login" replace />} />
          {/* Tenders premium (Bidding suite) */}
          <Route path="/tenders/bidding" element={isAuthenticated ? <W14TendersBiddingPipeline /> : <Navigate to="/login" replace />} />
          <Route path="/tenders/bid-preparation" element={isAuthenticated ? <W14TendersBidPreparation /> : <Navigate to="/login" replace />} />
          <Route path="/tenders/bid-outcomes" element={isAuthenticated ? <W14TendersBidOutcomes /> : <Navigate to="/login" replace />} />
          <Route path="/tenders/competitor-intel" element={isAuthenticated ? <W14TendersCompetitorIntel /> : <Navigate to="/login" replace />} />
          <Route path="/tenders/doc-library" element={isAuthenticated ? <W14TendersDocLibrary /> : <Navigate to="/login" replace />} />
          {/* QTO premium */}
          <Route path="/qto/4d5d" element={isAuthenticated ? <W14QTO4D5D /> : <Navigate to="/login" replace />} />
          <Route path="/qto/review" element={isAuthenticated ? <W14QTOReview /> : <Navigate to="/login" replace />} />
          <Route path="/qto/mapping-session/:sessionId" element={isAuthenticated ? <W14QTOMappingSession /> : <Navigate to="/login" replace />} />
          {/* Settings premium */}
          <Route path="/settings/crowd-pricing" element={isAuthenticated ? <W14SettingsCrowdPricing /> : <Navigate to="/login" replace />} />
          <Route path="/settings/dms-bridge" element={isAuthenticated ? <W14SettingsDMSBridge /> : <Navigate to="/login" replace />} />
          <Route path="/settings/regional" element={isAuthenticated ? <W14SettingsRegional /> : <Navigate to="/login" replace />} />
          <Route path="/settings/security" element={isAuthenticated ? <W14SettingsSecurity /> : <Navigate to="/login" replace />} />
          {/* Government premium */}
          <Route path="/government/dashboard" element={isAuthenticated ? <W14GovDashboard /> : <Navigate to="/login" replace />} />
          <Route path="/government/visas" element={isAuthenticated ? <W14GovVisas /> : <Navigate to="/login" replace />} />
          {/* Materials premium */}
          <Route path="/materials/warehouses" element={isAuthenticated ? <W14MaterialsWarehouses /> : <Navigate to="/login" replace />} />
          <Route path="/materials/takeoff" element={isAuthenticated ? <W14MaterialsTakeoff /> : <Navigate to="/login" replace />} />
          <Route path="/materials/site-consumption" element={isAuthenticated ? <W14MaterialsSiteConsumption /> : <Navigate to="/login" replace />} />
          <Route path="/materials/theft-detection" element={isAuthenticated ? <W14MaterialsTheftDetection /> : <Navigate to="/login" replace />} />
          {/* Billing premium */}
          <Route path="/billing/dashboard" element={isAuthenticated ? <W14BillingDashboard /> : <Navigate to="/login" replace />} />
          <Route path="/billing/invoices" element={isAuthenticated ? <W14BillingInvoices /> : <Navigate to="/login" replace />} />
          <Route path="/billing/upgrade" element={isAuthenticated ? <W14BillingUpgrade /> : <Navigate to="/login" replace />} />
          {/* === Wave 14 Agent POLISH END === */}
     </Routes>
        </Suspense>
      </BreadcrumbProvider>
      {/* === STAGE2-AGENT-B (Currency wiring) BEGIN === */}
      </CurrencyProvider>
      {/* === STAGE2-AGENT-B (Currency wiring) END === */}
    </BrowserRouter>
  )
}

export default App